import pluralize from 'pluralize';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { type AssetSearchFiltersCalculated, type Board, type Clip, type SortField } from '@air/api/types';
import { type GallerySection, GalleryView, type JustifiedBox } from '@air/component-gallery-view';
import { Spinner } from '@air/primitive-spinner';

import { useGallerySectionFooter } from '~/components/Gallery/useGallerySectionFooter';
import { ItemPickerBoardCard } from '~/components/ItemPicker/ItemPickerBoardCard';
import { ItemPickerGallerySectionHeader } from '~/components/ItemPicker/ItemPickerGallerySectionHeader';
import {
  SELECT_ASSET_MODAL_GRID_ITEM_TITLE_HEIGHT,
  SelectAssetModalGridItem,
} from '~/components/SelectAssetModal/SelectAssetModalContent/components/SelectAssetModalGridItem/SelectAssetModalGridItem';
import { useJustifiedGrid } from '~/components/Shared/JustifiedGrid';
import { useScrollElementContext } from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { getClipRatio } from '~/hooks/useClipRatios';
import { useClips } from '~/swr-hooks/clips/useClips';

const CLIPS_PAGE_LIMIT = 100;

const SECTION_HEADER_HEIGHT = 36;

/** Board cards keep the old grid's `grid-cols-4 gap-3` geometry. */
const BOARD_GRID_COLUMNS = 4;
const BOARD_GRID_GAP = 12;
const BOARD_ROW_VERTICAL_SPACING = 12;

/** Approximates the old `py-16` spinner / no-results block. */
const STATE_ROW_HEIGHT = 168;

/** Matches the old asset grid's `py-1` row padding. */
const ASSET_ROW_VERTICAL_SPACING = 8;

/** Copied from SelectAssetModalGrid so the picker's asset rows keep their exact layout. */
const ASSET_GRID_OPTIONS = {
  containerPadding: {
    left: 0,
    right: 0,
  },
  targetRowHeight: 160,
  boxSpacing: {
    horizontal: 4,
    vertical: 0,
  },
  additionalRowHeight: SELECT_ASSET_MODAL_GRID_ITEM_TITLE_HEIGHT,
};

export type ItemPickerGalleryViewBoardsProps = {
  /** True when clicking a board toggles selection; false when clicking navigates into the board. */
  areBoardsSelectable: boolean;
  boards: Board[];
  /** The API's total board count for the current view — the header labels with it so paginated
   * sections don't read "100 Boards" while more pages exist. */
  boardsTotal: number;
  hasMoreBoards: boolean;
  isLoadingBoards: boolean;
  onBoardClick: (board: Board) => void;
  /** Double-clicking a board browses into it (instead of toggling selection twice). */
  onBoardDoubleClick: (board: Board) => void;
  /** Absent when the current view has no boards to page through (e.g. the boards-less asset views). */
  onLoadMoreBoards?: () => void;
  /** Registers each card element with the picker's cross-section marquee (null on unmount). */
  registerBoardElement: (board: Board, element: HTMLElement | null) => void;
  selectedBoardIds: ReadonlySet<Board['id']>;
};

export type ItemPickerGalleryViewClipsProps = {
  /** False for board-only pickers: the assets section is omitted and no clips search runs. */
  areClipsSelectable: boolean;
  filters: AssetSearchFiltersCalculated;
  onToggleClip: (clip: Clip) => void;
  /** Registers each card element with the picker's cross-section marquee (null on unmount). */
  registerClipElement: (clip: Clip, element: HTMLElement | null) => void;
  search: string;
  selectedClips: Map<Clip['id'], Clip>;
  /** When omitted, useClips falls back to its default name-asc sort. */
  sortField?: SortField;
};

export type ItemPickerGalleryViewProps = ItemPickerGalleryViewBoardsProps & ItemPickerGalleryViewClipsProps;

/**
 * The picker's whole content area as ONE virtualized gallery: a fixed-column boards section and a
 * justified assets section flattened into a single row stream, each with a collapse-toggle header
 * and a load-more footer.
 */
export const ItemPickerGalleryView = memo(
  ({
    areBoardsSelectable,
    areClipsSelectable,
    boards,
    boardsTotal,
    filters,
    hasMoreBoards,
    isLoadingBoards,
    onBoardClick,
    onBoardDoubleClick,
    onLoadMoreBoards,
    onToggleClip,
    registerBoardElement,
    registerClipElement,
    search,
    selectedBoardIds,
    selectedClips,
    sortField,
  }: ItemPickerGalleryViewProps) => {
    const { width: containerWidth, ref: containerRef } = useResizeDetector<HTMLDivElement>();
    const { scrollElement } = useScrollElementContext();

    // GalleryView reads scrollElementRef.current, so the context element is adapted into a ref-shaped object.
    const scrollElementRef = useMemo(() => ({ current: scrollElement }), [scrollElement]);

    const [isBoardsSectionOpen, setIsBoardsSectionOpen] = useState(true);
    const [isAssetsSectionOpen, setIsAssetsSectionOpen] = useState(true);

    const handleToggleBoardsSection = useCallback(() => setIsBoardsSectionOpen((previous) => !previous), []);
    const handleToggleAssetsSection = useCallback(() => setIsAssetsSectionOpen((previous) => !previous), []);

    const { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useClips(
      { search, filters, limit: CLIPS_PAGE_LIMIT, sortField },
      { enabled: areClipsSelectable },
    );

    const clips = useMemo(() => data?.pages.flatMap((page) => page.data.clips) ?? [], [data]);
    const totalClips = data?.pages[0]?.data.total ?? 0;

    const clipsById = useMemo(() => new Map(clips.map((clip) => [clip.id, clip])), [clips]);

    // Clips that have registered a card element, kept so an unmount can ALWAYS unregister: a search
    // or refetch can drop a clip from clipsById before its card's null ref-callback runs, and a
    // lookup-gated unregister would strand a detached element in the marquee registry.
    const registeredClipsRef = useRef<Map<Clip['id'], Clip>>(new Map());

    // Grid items register by clip id; the picker's marquee registry needs the full clip resolved here.
    const handleRegisterClipElement = useCallback(
      (clipId: Clip['id'], element: HTMLElement | null) => {
        const clip = clipsById.get(clipId) ?? registeredClipsRef.current.get(clipId);

        if (!clip) {
          return;
        }

        if (element) {
          registeredClipsRef.current.set(clipId, clip);
        } else {
          registeredClipsRef.current.delete(clipId);
        }

        registerClipElement(clip, element);
      },
      [clipsById, registerClipElement],
    );

    const handleLoadMoreClips = useCallback(() => {
      fetchNextPage();
    }, [fetchNextPage]);

    const ratios = useMemo(() => clips.map((clip) => getClipRatio(clip)), [clips]);

    const { boxes: assetBoxes } = useJustifiedGrid({
      containerWidth: containerWidth ?? 0,
      ratios,
      options: ASSET_GRID_OPTIONS,
    });

    const boardBoxes = useMemo<JustifiedBox[]>(() => {
      if (!containerWidth) {
        return [];
      }

      const columnWidth = (containerWidth - (BOARD_GRID_COLUMNS - 1) * BOARD_GRID_GAP) / BOARD_GRID_COLUMNS;

      // The board card is its aspect-square thumbnail (the title overlays inside it), so the box
      // height equals the column width. top only needs to group boxes of one row to an equal value.
      return boards.map((_, index) => {
        const rowIndex = Math.floor(index / BOARD_GRID_COLUMNS);
        const columnIndex = index % BOARD_GRID_COLUMNS;

        return {
          aspectRatio: 1,
          height: columnWidth,
          left: columnIndex * (columnWidth + BOARD_GRID_GAP),
          top: rowIndex * (columnWidth + BOARD_GRID_GAP),
          width: columnWidth,
        };
      });
    }, [boards, containerWidth]);

    // The header labels with the API total (like the assets header), not just the loaded pages —
    // boards.length is the floor in case a total is stale or absent mid-refetch.
    const boardsCount = Math.max(boardsTotal, boards.length);

    const headerBox = useMemo<JustifiedBox>(
      () => ({ aspectRatio: 0, height: SECTION_HEADER_HEIGHT, left: 0, top: 0, width: containerWidth ?? 0 }),
      [containerWidth],
    );

    const stateRowBox = useMemo<JustifiedBox>(
      () => ({ aspectRatio: 0, height: STATE_ROW_HEIGHT, left: 0, top: 0, width: containerWidth ?? 0 }),
      [containerWidth],
    );

    const renderBoardsSectionHeader = useCallback(
      () => (
        <ItemPickerGallerySectionHeader
          isOpen={isBoardsSectionOpen}
          key="boards-header"
          label={`${boardsCount} ${pluralize('Board', boardsCount)}`}
          onToggle={handleToggleBoardsSection}
        />
      ),
      [boardsCount, handleToggleBoardsSection, isBoardsSectionOpen],
    );

    const renderAssetsSectionHeader = useCallback(
      () => (
        <ItemPickerGallerySectionHeader
          isOpen={isAssetsSectionOpen}
          key="assets-header"
          label={`${totalClips} ${pluralize('Asset', totalClips)}`}
          onToggle={handleToggleAssetsSection}
        />
      ),
      [handleToggleAssetsSection, isAssetsSectionOpen, totalClips],
    );

    const renderBoardItem = useCallback(
      (index: number) => {
        const board = boards[index];

        if (!board) {
          return null;
        }

        return (
          <ItemPickerBoardCard
            board={board}
            isSelectable={areBoardsSelectable}
            isSelected={selectedBoardIds.has(board.id)}
            key={board.id}
            onClick={onBoardClick}
            onDoubleClick={onBoardDoubleClick}
            registerElement={registerBoardElement}
          />
        );
      },
      [areBoardsSelectable, boards, onBoardClick, onBoardDoubleClick, registerBoardElement, selectedBoardIds],
    );

    const renderAssetItem = useCallback(
      (index: number) => {
        const clip = clips[index];
        const box = assetBoxes[index];

        if (!clip || !box) {
          return null;
        }

        return (
          <SelectAssetModalGridItem
            clip={clip}
            height={box.height}
            isSelected={selectedClips.has(clip.id)}
            key={clip.id}
            onToggle={onToggleClip}
            registerCardElement={handleRegisterClipElement}
            width={box.width}
          />
        );
      },
      [assetBoxes, clips, handleRegisterClipElement, onToggleClip, selectedClips],
    );

    const renderClipsSpinner = useCallback(
      () => (
        <div className="flex h-full items-center justify-center" key="clips-spinner">
          <Spinner className="size-10 ads:text-ads-cyan-10 text-teal-9" />
        </div>
      ),
      [],
    );

    const renderNoResults = useCallback(
      () => (
        <div
          className="flex h-full items-center justify-center ads:text-metadata text-gray-10"
          data-testid="ITEM_PICKER_NO_RESULTS"
          key="no-results"
        >
          No results found
        </div>
      ),
      [],
    );

    const boardsFooter = useGallerySectionFooter({
      containerWidth: containerWidth ?? 0,
      hasMore: hasMoreBoards,
      isLoading: isLoadingBoards,
      loadMore: onLoadMoreBoards,
    });

    const assetsFooter = useGallerySectionFooter({
      containerWidth: containerWidth ?? 0,
      hasMore: !!hasNextPage,
      isLoading: isFetchingNextPage,
      loadMore: handleLoadMoreClips,
    });

    const sections = useMemo(() => {
      const nextSections: GallerySection[] = [];

      if (boardsCount > 0) {
        nextSections.push({
          header: { box: headerBox, render: renderBoardsSectionHeader },
          items: {
            boxes: isBoardsSectionOpen ? boardBoxes : [],
            render: renderBoardItem,
            verticalItemsSpacing: BOARD_ROW_VERTICAL_SPACING,
          },
          footer: isBoardsSectionOpen ? boardsFooter : undefined,
        });
      } else if (!areClipsSelectable && isLoadingBoards) {
        // Board-only pickers have no clips spinner; keep the first Boards.list pending so
        // "No results found" does not flash before the query resolves (FOUND-813).
        nextSections.push({
          items: { boxes: [stateRowBox], render: renderClipsSpinner, verticalItemsSpacing: 0 },
        });
      } else if (!areClipsSelectable) {
        nextSections.push({
          items: { boxes: [stateRowBox], render: renderNoResults, verticalItemsSpacing: 0 },
        });
      }

      if (areClipsSelectable) {
        if (isPending) {
          nextSections.push({
            items: { boxes: [stateRowBox], render: renderClipsSpinner, verticalItemsSpacing: 0 },
          });
        } else if (totalClips === 0) {
          nextSections.push({
            items: { boxes: [stateRowBox], render: renderNoResults, verticalItemsSpacing: 0 },
          });
        } else {
          nextSections.push({
            header: { box: headerBox, render: renderAssetsSectionHeader },
            items: {
              boxes: isAssetsSectionOpen ? assetBoxes : [],
              render: renderAssetItem,
              verticalItemsSpacing: ASSET_ROW_VERTICAL_SPACING,
            },
            footer: isAssetsSectionOpen ? assetsFooter : undefined,
          });
        }
      }

      return nextSections;
    }, [
      areClipsSelectable,
      assetBoxes,
      assetsFooter,
      boardBoxes,
      boardsCount,
      boardsFooter,
      headerBox,
      isAssetsSectionOpen,
      isBoardsSectionOpen,
      isLoadingBoards,
      isPending,
      renderAssetItem,
      renderAssetsSectionHeader,
      renderBoardItem,
      renderBoardsSectionHeader,
      renderClipsSpinner,
      renderNoResults,
      stateRowBox,
      totalClips,
    ]);

    return (
      <div ref={containerRef}>
        {!!containerWidth && (
          <GalleryView scrollElementRef={scrollElementRef} sections={sections} width={containerWidth} />
        )}
      </div>
    );
  },
);

ItemPickerGalleryView.displayName = 'ItemPickerGalleryView';
